import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentRef,
} from "react";
import ReactECharts from "echarts-for-react";
import type { TechTrendPoint, TechTrendResponse } from "@/types/jobMarket";

interface Props {
  data: TechTrendResponse;
}

const TREND_TECHS = new Set([
  "ClickHouse",
  "PostgreSQL",
  "MySQL",
  "MongoDB",
  "Elasticsearch",
  "Redis",
  "Cassandra",
  "Greenplum",
  "Vertica",
  "Oracle",
  "MS SQL Server",
  "Apache Airflow",
  "Prefect",
  "Dagster",
  "Luigi",
  "Apache Spark",
  "Apache Flink",
  "Apache Beam",
  "Trino",
  "Apache Nifi",
]);

const TECH_COLOR: Record<string, string> = {
  PostgreSQL: "#3b82f6",
  ClickHouse: "#0abf53",
  Greenplum: "#1d4ed8",
  MySQL: "#06b6d4",
  MongoDB: "#16a34a",
  Oracle: "#dc2626",
  "MS SQL Server": "#7c3aed",
  Elasticsearch: "#0ea5e9",
  Redis: "#f43f5e",
  Cassandra: "#64748b",
  Vertica: "#0891b2",
  "Apache Airflow": "#8b5cf6",
  Prefect: "#a78bfa",
  Dagster: "#6d28d9",
  Luigi: "#c4b5fd",
  "Apache Spark": "#f59e0b",
  Trino: "#f97316",
  "Apache Flink": "#fbbf24",
  "Apache Nifi": "#fb923c",
  "Apache Beam": "#fde047",
};

const HISTORY_COLS = 16;
const FORECAST_QUARTERS = 4;
const MAX_COLUMNS = HISTORY_COLS + FORECAST_QUARTERS;

function nextQuarterPeriod(period: string): string {
  const d = new Date(period);
  d.setMonth(d.getMonth() + 3);
  return d.toISOString().slice(0, 10);
}

function formatPeriod(p: string): string {
  const d = new Date(p);
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

const AFTER_Q2_2026_START = new Date("2026-07-01T00:00:00");

function isAfterQ2_2026(periodIso: string): boolean {
  return new Date(periodIso) >= AFTER_Q2_2026_START;
}

function fillGaps(rawData: TechTrendPoint[], techs: string[]): TechTrendPoint[] {
  if (rawData.length < 2) return rawData;

  const filled: TechTrendPoint[] = [rawData[0]];

  for (let i = 1; i < rawData.length; i++) {
    const prev = filled[filled.length - 1];
    const curr = rawData[i];
    const prevDate = new Date(String(prev.period));
    const currDate = new Date(String(curr.period));
    const monthsDiff =
      (currDate.getFullYear() - prevDate.getFullYear()) * 12 +
      (currDate.getMonth() - prevDate.getMonth());
    const gaps = Math.round(monthsDiff / 3) - 1;

    for (let q = 1; q <= gaps; q++) {
      const t = q / (gaps + 1);
      const d = new Date(prevDate);
      d.setMonth(d.getMonth() + q * 3);
      const point: TechTrendPoint = {
        period: d.toISOString().slice(0, 10),
        total: Math.round(
          (Number(prev.total) + t * (Number(curr.total) - Number(prev.total))) as number
        ),
      };
      for (const tech of techs) {
        const pv = Number(prev[tech] ?? 0);
        const cv = Number(curr[tech] ?? 0);
        point[tech] = Math.round((pv + t * (cv - pv)) * 10) / 10;
      }
      filled.push(point);
    }
    filled.push(curr);
  }

  return filled;
}

function addForecast(data: TechTrendPoint[], techs: string[], quarters: number): TechTrendPoint[] {
  const result = [...data];
  const WINDOW = 4;

  for (let i = 0; i < quarters; i++) {
    const window = result.slice(-WINDOW);
    const next: TechTrendPoint = {
      period: nextQuarterPeriod(String(result[result.length - 1].period)),
      total: window[window.length - 1].total,
    };
    for (const tech of techs) {
      const vals = window.map((p) => Number(p[tech] ?? 0));
      const slope =
        vals.length > 1 ? (vals[vals.length - 1] - vals[0]) / (vals.length - 1) : 0;
      next[tech] = Math.max(0, Math.round((vals[vals.length - 1] + slope) * 10) / 10);
    }
    result.push(next);
  }

  return result;
}

export function StackedTrendChart({ data }: Props) {
  const [isolatedTech, setIsolatedTech] = useState<string | null>(null);
  const chartRef = useRef<ComponentRef<typeof ReactECharts>>(null);
  const layoutRef = useRef({
    periods: [] as string[],
    periodData: [] as TechTrendPoint[],
    firstPeriod: "",
    lastReal: "",
  });

  if (!data.has_data || !data.data.length) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
        Недостаточно данных для построения динамики. Соберите данные за несколько периодов.
      </div>
    );
  }

  const techs = data.techs.filter((t) => TREND_TECHS.has(t));

  const filled = fillGaps(data.data, techs);
  const historicalCount = filled.length;

  const withForecast = addForecast(filled, techs, FORECAST_QUARTERS);

  const periodData = withForecast.slice(-MAX_COLUMNS);
  const offsetFromFull = withForecast.length - periodData.length;
  const periods = periodData.map((d) => formatPeriod(String(d.period)));

  const forecastStartIdx = historicalCount - offsetFromFull;

  const getOpacity = (idx: number): number => {
    const absIdx = idx + offsetFromFull;
    if (absIdx < historicalCount) return 1;
    return isAfterQ2_2026(String(periodData[idx].period)) ? 0.45 : 1;
  };

  const normalized: Record<string, number[]> = Object.fromEntries(techs.map((t) => [t, []]));
  for (const row of periodData) {
    const total = techs.reduce((s, t) => s + Number(row[t] ?? 0), 0);
    for (const tech of techs) {
      const v = Number(row[tech] ?? 0);
      normalized[tech].push(total > 0 ? Math.round((v / total) * 1000) / 10 : 0);
    }
  }

  const legendSelected = Object.fromEntries(
    techs.map((t) => [t, isolatedTech === null || t === isolatedTech])
  );

  const series = techs.map((tech) => ({
    name: tech,
    type: "bar" as const,
    stack: "total",
    itemStyle: {
      color: TECH_COLOR[tech] ?? "#94a3b8",
    },
    emphasis: { focus: "series" as const },
    label: { show: false },
    data: normalized[tech].map((val, idx) => ({
      value: val,
      itemStyle: {
        opacity: getOpacity(idx),
      },
    })),
  }));

  const firstPeriod = formatPeriod(String(data.data[0]?.period ?? periodData[0].period));
  const lastReal = formatPeriod(String(data.data[data.data.length - 1]?.period ?? ""));

  layoutRef.current = { periods, periodData, firstPeriod, lastReal };

  const paintForecastVerticalLines = useCallback(() => {
    const inst = chartRef.current?.getEchartsInstance?.();
    if (!inst) return;
    const { periods: p, periodData: pd, firstPeriod: fp, lastReal: lr } = layoutRef.current;
    const lineStartIdx = pd.findIndex((row) => isAfterQ2_2026(String(row.period)));
    const subtitleGraphic = {
      id: "stacked-subtitle",
      type: "text" as const,
      left: "center",
      bottom: 52,
      style: {
        text: `Аналитика за 5 лет · ${fp} — ${lr}`,
        fill: "#9ca3af",
        fontSize: 11,
      },
    };
    if (lineStartIdx < 0) {
      inst.setOption({ graphic: [subtitleGraphic] }, { replaceMerge: ["graphic"] });
      return;
    }
    const lineElements: unknown[] = [];
    for (let i = lineStartIdx; i < pd.length; i++) {
      const top = inst.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [p[i], 100]);
      const bottom = inst.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [p[i], 0]);
      lineElements.push({
        type: "line" as const,
        shape: { x1: top[0], y1: top[1], x2: bottom[0], y2: bottom[1] },
        style: { stroke: "rgba(0,0,0,0.14)", lineWidth: 1 },
        silent: true,
        z: 0,
      });
    }
    inst.setOption({ graphic: [subtitleGraphic, ...lineElements] }, { replaceMerge: ["graphic"] });
  }, []);

  useEffect(() => {
    const onResize = () => paintForecastVerticalLines();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [paintForecastVerticalLines, periods, periodData, firstPeriod, lastReal]);

  const option = {
    color: techs.map((t) => TECH_COLOR[t] ?? "#94a3b8"),
    tooltip: {
      trigger: "axis" as const,
      appendToBody: true,
      confine: false,
      axisPointer: { type: "shadow" as const },
      formatter: (params: { dataIndex?: number; axisValue?: string; value: number; color: string; seriesName: string }[]) => {
        const idx = params[0]?.dataIndex ?? 0;
        const isForecast = idx >= forecastStartIdx;
        let badge = "";
        if (isForecast)
          badge = ` <span style="color:#f59e0b;font-size:10px;font-weight:600">прогноз</span>`;
        let html = `<div style="font-weight:600;margin-bottom:2px">${params[0].axisValue}${badge}</div>`;
        html += `<div style="color:#9ca3af;font-size:10px;margin-bottom:4px">доля внутри группы · абсолютные % выше</div>`;
        const sorted = [...params].filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
        for (const p of sorted) {
          html += `<div style="display:flex;align-items:center;gap:6px;margin:1px 0">
            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${p.color}"></span>
            <span style="color:#374151;min-width:120px">${p.seriesName}</span>
            <strong style="margin-left:auto">${p.value}%</strong>
          </div>`;
        }
        return html;
      },
    },
    legend: {
      type: "scroll" as const,
      bottom: 0,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { color: "#6b7280", fontSize: 11 },
      selected: legendSelected,
    },
    grid: { left: 48, right: 16, top: 28, bottom: 90 },
    xAxis: {
      type: "category" as const,
      data: periods,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value" as const,
      max: 100,
      axisLabel: { formatter: "{value}%", color: "#9ca3af", fontSize: 11 },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series,
  };

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height: 420, width: "100%" }}
      onEvents={{
        finished: () => paintForecastVerticalLines(),
        legendselectchanged: (params: { name: string }) => {
          const clicked: string = params.name;
          setIsolatedTech((prev) => (prev === clicked ? null : clicked));
        },
      }}
      notMerge
    />
  );
}
